import { useCallback, useSyncExternalStore } from 'react'
import { useAccount, useConfig, useSwitchChain } from 'wagmi'
import { useAppKitNetwork } from '@reown/appkit/react'
import { APP_CHAIN_NAME, APP_NETWORK, CHAIN_ID } from '../utils/contractAddresses'
// Wallets surface a declined switch in several shapes; the tx engine already
// owns that list — one copy, so it cannot drift from what the journal records.
import { isUserRejection } from '../tx/errors'

/**
 * Force the wallet onto the chain this build talks to, before any signature or
 * write is requested — and prove it landed.
 *
 * Why this exists: wagmi's `getConnectorClient()` — which every `signMessage` /
 * `writeContract` goes through — compares `connector.getChainId()` against the
 * chain id cached on the connection with `!==`, and throws
 * `ConnectorChainMismatchError` when they differ. Two things make that fire on a
 * wallet the user believes is fine:
 *
 *  1. The two sides come from different places. The AppKit WalletConnect
 *     connector reports AppKit's *active network*, not the wallet's; the cached
 *     connection id comes from whatever `change` event last carried it.
 *  2. `!==` is strict, and AppKit's "unsupported network" placeholder carries a
 *     STRING id — which is why the error can print the SAME id on both sides
 *     ("current 11155111 … expected 11155111") while being a real wrong-network.
 *
 * Switching repairs both: AppKit's `switchNetwork` resets its active network
 * (side 1) and the connector re-emits `change` with `Number(chainId)` (side 2).
 */

/** AppKit's sentinel name for a chain outside `networks` (ConstantsUtil). */
const UNSUPPORTED_NETWORK_NAME = 'Unknown Network'

/**
 * Names for the chains a user is realistically sitting on when they land here.
 * AppKit reports "Unknown Network" for anything outside `networks` — which is
 * every wrong chain, by definition — so without this the banner could only say
 * "a different network". Naming the chain is the difference between a warning
 * and an instruction.
 */
const CHAIN_NAMES = {
  1: 'Ethereum',
  10: 'Optimism',
  56: 'BNB Chain',
  137: 'Polygon',
  8453: 'Base',
  42161: 'Arbitrum One',
  43114: 'Avalanche',
  80002: 'Polygon Amoy',
  11155111: 'Sepolia',
}

/** Best available name for whatever chain the wallet is actually on. */
function describeChain(chainId, appKitName) {
  if (appKitName && appKitName !== UNSUPPORTED_NETWORK_NAME) return appKitName
  const id = Number(chainId)
  if (!Number.isFinite(id)) return null
  return CHAIN_NAMES[id] || `chain ${id}`
}

/**
 * A wallet switch on mobile WalletConnect needs the user to leave the browser,
 * act in the wallet app, and come back. That request can hang forever if they
 * never return. Bound it so the UI falls back to the banner instead of freezing.
 */
const SWITCH_TIMEOUT_MS = 30_000

/** How long to let the connector catch up after a switch resolves. */
const VERIFY_ATTEMPTS = 10
const VERIFY_INTERVAL_MS = 200

/**
 * How long to wait for AppKit's switch to land before falling back to wagmi's.
 * Short: this is the gap between the wallet confirming and the state settling,
 * not a wait on the user. Too long and a genuinely-failed AppKit switch stalls;
 * too short and we fire a second, needless wallet prompt.
 */
const POST_SWITCH_ATTEMPTS = 5

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * wagmi's `ConnectorChainMismatchError`, matched by message so it survives the
 * duplicate @wagmi/core copies a bundle can end up with. Its text is the one the
 * mobile login failed on: "the current chain of the connector (id: X) does not
 * match the connection's chain (id: X)" — the same id printed twice.
 *
 * Exported because both chokepoints (login and the shared write hook) need to
 * recognise it, and a second copy of this regex would be a second thing to keep
 * in step with wagmi.
 */
export function isChainMismatchError(error) {
  if (!error) return false
  const text = `${error.name || ''} ${error.shortMessage || ''} ${error.message || ''}`
  return /does not match the connection's chain|chain mismatch|ChainMismatch/i.test(text)
}

function withTimeout(promise, ms, label) {
  let timer
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(`${label} timed out`)), ms)
  })
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer))
}

/* ── shared across every caller ──────────────────────────────────────────
 * This hook runs in ~20 places at once: the banner, the login effect, and one
 * instance per mounted `usePersistentWriteContract`. Per-instance state would
 * mean two of them can start a switch at the same moment — the login effect
 * auto-switching while the user taps the banner's button — and the wallet gets
 * TWO `wallet_switchEthereumChain` requests. On mobile that is two deep-links
 * out to the wallet app. So the in-flight switch and the "is switching" flag
 * live at module scope: one switch at a time, and every instance sees it.
 */
let inFlightSwitch = null
let switching = false
const switchListeners = new Set()

function setSwitching(next) {
  if (switching === next) return
  switching = next
  switchListeners.forEach((l) => l())
}

const subscribeSwitching = (listener) => {
  switchListeners.add(listener)
  return () => switchListeners.delete(listener)
}

const getSwitching = () => switching

/** Test seam — module state would otherwise leak between cases. */
export const __resetChainSwitchState = () => {
  inFlightSwitch = null
  switching = false
  switchListeners.clear()
}

export function useEnsureChain() {
  const { connector, chainId: connectionChainId, isConnected } = useAccount()
  const config = useConfig()
  const { switchChainAsync } = useSwitchChain()
  const { switchNetwork, caipNetwork } = useAppKitNetwork()
  const isSwitching = useSyncExternalStore(subscribeSwitching, getSwitching, getSwitching)

  /**
   * True only when the wallet is REALLY on another chain — the one thing worth
   * telling the user about.
   *
   * Compared by VALUE, not identity, and that distinction is the whole lesson of
   * this file. wagmi's `getConnectorClient` compares strictly, so a cached id of
   * `"11155111"` (string) trips it even though the wallet is exactly where it
   * should be. Mirroring that strictness here was a mistake: on a real phone it
   * put up a banner reading "Wrong network — FRDM runs on Sepolia. Your wallet is
   * on Sepolia", then span on "Switching…" forever and blocked login, because no
   * amount of switching can turn a string into a number.
   *
   * A type mismatch is wagmi's bookkeeping problem, not the user's. It is
   * repaired silently by `resyncConnectionType` below; only a genuine
   * different-chain situation reaches the banner.
   */
  const isWrongChain =
    isConnected &&
    connectionChainId !== undefined &&
    Number(connectionChainId) !== CHAIN_ID

  /**
   * Read the chain the connector will actually report to wagmi. A live call, not
   * React state: state lags the switch by at least a tick, so verifying against
   * it would report success before the repair had landed.
   */
  const readConnectorChainId = useCallback(async () => {
    if (!connector?.getChainId) return undefined
    try {
      return await connector.getChainId()
    } catch (e) {
      return undefined
    }
  }, [connector])

  /**
   * wagmi's cached connection chain id, read LIVE from the store.
   *
   * `useAccount().chainId` is a render value: right after a switch it still
   * holds the old chain for a tick. Deciding "do we need to switch?" from it
   * asked the wallet to switch to a chain it had just moved to — a second,
   * pointless prompt, seen in a real browser as switchRequests: 2. This reads
   * the same field wagmi's own `getConnectorClient` will compare against, at
   * the moment we compare it.
   */
  const readConnectionChainId = useCallback(() => {
    try {
      return config.state.connections.get(config.state.current)?.chainId
    } catch (e) {
      return connectionChainId
    }
  }, [config, connectionChainId])

  /**
   * Both sides are on the right chain BY VALUE — the terminating condition.
   *
   * Must not be strict. A strict test never becomes true when wagmi is holding
   * `"11155111"`, so the verify loop below ran to exhaustion, reported "the
   * switch didn't go through", and left the user stuck — on a wallet that was
   * already on the correct network. Seen on a real device.
   */
  const isSettled = useCallback(
    async () =>
      Number(await readConnectorChainId()) === CHAIN_ID &&
      Number(readConnectionChainId()) === CHAIN_ID,
    [readConnectorChainId, readConnectionChainId]
  )

  /**
   * Right chain, wrong TYPE — what actually makes wagmi throw
   * `ConnectorChainMismatchError` with the same id printed twice.
   */
  const needsTypeResync = useCallback(async () => {
    const connectorId = await readConnectorChainId()
    if (connectorId === undefined) return false
    const connectionId = readConnectionChainId()
    const bothOnValue =
      Number(connectorId) === CHAIN_ID && Number(connectionId) === CHAIN_ID
    return bothOnValue && !(connectorId === CHAIN_ID && connectionId === CHAIN_ID)
  }, [readConnectorChainId, readConnectionChainId])

  const settleWithin = useCallback(
    async (attempts) => {
      for (let i = 0; i < attempts; i += 1) {
        if (await isSettled()) return true
        if (i < attempts - 1) await delay(VERIFY_INTERVAL_MS)
      }
      return false
    },
    [isSettled]
  )

  /**
   * ORDER MATTERS, and it is the opposite of what it looks like it should be.
   *
   * Both wagmi's switch and AppKit's send `wallet_switchEthereumChain`, so each
   * one costs the user a wallet prompt — on mobile, a deep-link out to the wallet
   * app and back. Firing both unconditionally cost TWO prompts on every switch;
   * measured in a real browser as switchRequests: 2.
   *
   * wagmi goes FIRST because it is the only one that tells the truth about a
   * refusal: AppKit's `ChainController.switchActiveNetwork` swallows the wallet's
   * rejection unless `throwOnFailure` is passed, and `useAppKitNetwork()` gives
   * us no way to pass it. Leading with AppKit meant a user who tapped "reject"
   * was immediately asked a second time, because "declined" and "didn't land"
   * looked identical from outside.
   *
   * AppKit's switch is kept as the FALLBACK — it repairs AppKit's own active
   * network, which is what the WalletConnect connector reports as its chain.
   */
  const performSwitch = useCallback(async () => {
    try {
      await withTimeout(
        switchChainAsync({ chainId: CHAIN_ID }),
        SWITCH_TIMEOUT_MS,
        'Network switch'
      )
    } catch (e) {
      console.error('Chain switch failed', e)
      if (isUserRejection(e)) {
        // They said no. Asking again through the other path is just a second
        // prompt for the same answer — the banner's button is the retry.
        return isSettled()
      }
    }

    if (await settleWithin(POST_SWITCH_ATTEMPTS)) return true

    try {
      // Not a companion to the call above — the fallback for when it did not
      // land, and the only thing that fixes AppKit's own idea of the network.
      await withTimeout(switchNetwork(APP_NETWORK), SWITCH_TIMEOUT_MS, 'Network switch')
    } catch (e) {
      console.warn('AppKit switchNetwork failed', e?.message)
    }

    if (await settleWithin(VERIFY_ATTEMPTS)) return true

    console.error(`Chain switch did not land; connector still off ${CHAIN_ID}`)
    return false
  }, [isSettled, settleWithin, switchChainAsync, switchNetwork])

  /**
   * Nudge wagmi into re-caching the chain id as a NUMBER.
   *
   * The wallet is already on the right chain here; only the stored type is
   * wrong. `switchChain` is the lever because the connector answers it by
   * emitting `change` with `Number(chainId)`. Best-effort by design: if it does
   * not take, the caller proceeds anyway and the signature paths retry once on
   * `ConnectorChainMismatchError`. Blocking on this is what froze a real phone
   * on "Switching…" — a stuck user is a worse outcome than one retried prompt.
   */
  const resyncConnectionType = useCallback(async () => {
    try {
      await withTimeout(
        switchChainAsync({ chainId: CHAIN_ID }),
        SWITCH_TIMEOUT_MS,
        'Chain resync'
      )
    } catch (e) {
      console.warn('Chain type resync failed; continuing anyway', e?.message)
    }
  }, [switchChainAsync])

  const ensureChain = useCallback(async () => {
    if (!connector) return false

    const current = await readConnectorChainId()
    if (current === undefined) {
      // A connector that cannot report its chain cannot be checked; let the
      // caller proceed rather than blocking login on a probe.
      return true
    }
    // Both sides read live — see readConnectionChainId for why a render value
    // here caused a redundant second wallet prompt.
    if (current === CHAIN_ID && readConnectionChainId() === CHAIN_ID) {
      return true
    }

    // RIGHT chain, wrong type. Repair quietly and let the caller through — this
    // is not a wrong-network situation and must never reach the banner, spin
    // forever, or block login. A real device sat here saying "your wallet is on
    // Sepolia" while refusing to use Sepolia.
    if (await needsTypeResync()) {
      if (!inFlightSwitch) {
        setSwitching(true)
        inFlightSwitch = resyncConnectionType().finally(() => {
          inFlightSwitch = null
          setSwitching(false)
        })
      }
      await inFlightSwitch
      return true
    }

    // Join a switch already in progress instead of starting a second one. Two
    // concurrent callers (login auto-switching while the user taps the banner)
    // would otherwise fire two wallet requests — two deep-links on mobile.
    if (inFlightSwitch) return inFlightSwitch

    setSwitching(true)
    inFlightSwitch = performSwitch().finally(() => {
      inFlightSwitch = null
      setSwitching(false)
    })
    return inFlightSwitch
  }, [connector, readConnectionChainId, readConnectorChainId, needsTypeResync, resyncConnectionType, performSwitch])

  return {
    ensureChain,
    isWrongChain,
    isSwitching,
    chainId: CHAIN_ID,
    chainName: APP_CHAIN_NAME,
    /** What the wallet is on right now, for "your wallet is on X" copy. */
    currentChainName: describeChain(connectionChainId ?? caipNetwork?.id, caipNetwork?.name),
  }
}

export default useEnsureChain
